import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";
import avatar1 from "../../../images/avatar/img01.jpg";
import avatar2 from "../../../images/avatar/img02.jpg";
import avatar3 from "../../../images/avatar/img03.jpg";
import avatar4 from "../../../images/avatar/img04.jpg";

interface Testimonial {
  id: number;
  name: string;
  company: string;
  rating: number;
  review: string;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Arjun Mehta",
    company: "TechFlow Solutions",
    rating: 5,
    review:
      "WebTech built an AI chatbot that reduced our support tickets by 50%. The team understood our needs and delivered a seamless solution.",
    avatar: avatar1,
  },
  {
    id: 2,
    name: "Sneha Kapoor",
    company: "RetailMax",
    rating: 5,
    review:
      "Our WhatsApp chatbot handles 80% of customer queries automatically. Sales and customer satisfaction both improved significantly.",
    avatar: avatar2,
  },
  {
    id: 3,
    name: "David Chen",
    company: "HealthFirst Clinic",
    rating: 5,
    review:
      "The appointment booking bot transformed our patient experience. Professional team, fast delivery, and excellent ongoing support.",
    avatar: avatar3,
  },
  {
    id: 4,
    name: "Meera Joshi",
    company: "TravelEase Agency",
    rating: 5,
    review:
      "Lead conversion jumped 40% after deploying WebTech's AI sales assistant. Highly recommend for any business looking to automate growth.",
    avatar: avatar4,
  },
];

const StarRating = ({ rating }: { rating: number }) => (
  <div className="cbot-testimonial__stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <svg key={i} viewBox="0 0 20 20" aria-hidden="true">
        <path
          d="M10 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 15.8 4.2 17.9l.9-5.4L1.2 7.7l5.4-.8L10 2Z"
          fill={i < rating ? "#fbbf24" : "rgba(255,255,255,0.15)"}
        />
      </svg>
    ))}
  </div>
);

const ChatBotTestimonials: React.FC = () => {
  const swiperRef = useRef<SwiperType | null>(null);

  return (
    <AnimatedSection as="section" className="cbot-section cbot-section--alt" aria-labelledby="cbot-testimonials-title">
      <div className="cbot-container">
        <SectionHeader
          eyebrow="Testimonials"
          title="What Our Clients Say"
          text="Trusted by businesses worldwide for intelligent chatbot development."
        />
      </div>

      <AnimatedSection className="cbot-testimonials__slider-wrap" delay={0.1}>
        <button
          type="button"
          className="cbot-testimonials__nav cbot-testimonials__nav--prev"
          aria-label="Previous testimonial"
          onClick={() => swiperRef.current?.slidePrev()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <Swiper
          modules={[Autoplay, Pagination]}
          loop
          speed={600}
          spaceBetween={24}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          breakpoints={{
            1200: { slidesPerView: 3 },
            768: { slidesPerView: 2 },
            0: { slidesPerView: 1 },
          }}
          className="cbot-testimonials__slider"
        >
          {testimonials.map((item) => (
            <SwiperSlide key={item.id}>
              <article className="cbot-testimonial-card">
                <div className="cbot-testimonial-card__author">
                  <img src={item.avatar} alt="" className="cbot-testimonial-card__avatar" />
                  <div>
                    <h3>{item.name}</h3>
                    <span>{item.company}</span>
                  </div>
                </div>
                <StarRating rating={item.rating} />
                <p>&ldquo;{item.review}&rdquo;</p>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          type="button"
          className="cbot-testimonials__nav cbot-testimonials__nav--next"
          aria-label="Next testimonial"
          onClick={() => swiperRef.current?.slideNext()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </AnimatedSection>
    </AnimatedSection>
  );
};

export default ChatBotTestimonials;
